"use client"

import {useEffect, useRef} from "react";
import {useAppSelector} from "@/lib/hooks";
import {useAttachSmsTagsMutation} from "@/lib/api/wallet/smsTagApi";
import {clearSmsTags, MAX_SMS_TAGS, readSmsTags} from "@/lib/utils/smsTags";

/**
 * Attaches captured SMS campaign tags (AC-695) to the authenticated player.
 * Mounted once in the website layout alongside <CioInitializer/> / <FcmInitializer/>.
 *
 * Tags are captured from the `?tag=` query param by <RouteObserver/> into the
 * `smsTags` localStorage set. Once a player is authenticated — fresh login,
 * registration, or a returning logged-in session (userId hydrated on load) —
 * we flush that set to the backend and clear it on success; on failure the tags
 * stay for the next trigger (next session / re-login).
 */
export default function SmsTagInitializer() {
    const userId = useAppSelector((state) => state.auth.userId);
    const [attachSmsTags] = useAttachSmsTagsMutation();
    const inFlight = useRef(false);

    useEffect(() => {
        if (!userId || typeof window === "undefined" || inFlight.current) return;

        const tags = readSmsTags();
        if (tags.length === 0) return;

        inFlight.current = true;
        attachSmsTags({tags: tags.slice(0, MAX_SMS_TAGS)})
            .unwrap()
            .then(() => clearSmsTags())
            .catch(() => {
                // keep the tags for a later trigger (next session / re-login)
            })
            .finally(() => {
                inFlight.current = false;
            });
    }, [userId, attachSmsTags]);

    return null;
}
